// client/src/routes/demo.tsx — the sample validated-search route (doc 05 UI-001 AC2).
//
// This route exists to prove the mechanism that 04 §9.2.3 makes NORMATIVE for every LIST/BOARD
// screen: filters, pagination, sort and addressable overlays live in the URL as Zod-validated
// search params, so lists are shareable, refresh-proof, and browser Back is a filter-undo.
// The real list screens (SCR-PROP-001, SCR-RENT-001, …) copy this shape; they do not reinvent it.
//
// It also demonstrates the canonical overlay rule: closing an overlay strips ONLY its own param.
//
// The schema below stays LANGUAGE-FREE (04 §5.4): a Zod `error:` carries a `validation.*` KEY
// that FormField resolves at render, never a Romanian sentence. That is what makes Appendix B
// (adding English) a zero-schema-change PR — see src/i18n/index.ts.

// ── DEVELOPMENT ONLY (D-2634, amending D-314) ─────────────────────────────────────────────
// A production build answers this path with SCR-SYS-404, exactly as it answers a path that was
// never a route, and carries none of the page itself — only the search schema below stays, since
// the router validates a search before it asks the guard. `beforeLoad` throws `notFound()` before
// anything renders, and `import.meta.env.DEV` — replaced by the literal `false` when Vite builds
// for production — leaves the component nothing to point at, so Rollup drops it and everything
// only it uses. `vite` (the dev server) and Vitest both run in development mode and keep the page.

import { createFileRoute, notFound, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

// The §9.2.3 pattern, field for field.
const demoSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().max(200).default(25),
  sort: z.string().optional(), // allowlisted fields server-side (02 §7.4)
  q: z.string().trim().optional(),
  status: z.string().optional(),
  // A canonical addressable overlay param, e.g. ?explain=$obligationId (SCR-FISC-007).
  explain: z.string().optional(),
});

export const Route = createFileRoute('/demo')({
  // Validation runs BEFORE `beforeLoad`, so in production a malformed search would reach the
  // error screen rather than the 404 — which would say the route exists. There the schema falls
  // back to its defaults instead, and the guard below answers.
  validateSearch: import.meta.env.DEV
    ? demoSearchSchema
    : demoSearchSchema.catch(() => demoSearchSchema.parse({})),
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  component: import.meta.env.DEV ? DemoComponent : undefined,
});

function DemoComponent() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { t } = useTranslation();

  return (
    <main>
      <h1>{t('dev.demo.title')}</h1>

      <p>{t('dev.demo.lead')}</p>

      <pre>{JSON.stringify(search, null, 2)}</pre>

      <button
        type="button"
        onClick={() => {
          void navigate({ search: (prev) => ({ ...prev, page: prev.page + 1 }) });
        }}
      >
        {t('dev.demo.nextPage')}
      </button>

      <button
        type="button"
        onClick={() => {
          void navigate({ search: (prev) => ({ ...prev, explain: 'demo-obligation-id' }) });
        }}
      >
        {t('dev.demo.openOverlay')}
      </button>

      <button
        type="button"
        onClick={() => {
          // Closing an overlay strips only its own param — filters and paging survive.
          void navigate({ search: (prev) => ({ ...prev, explain: undefined }) });
        }}
      >
        {t('dev.demo.closeOverlay')}
      </button>
    </main>
  );
}
